import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BRAND, IMG } from "@/data/site";
import { ButtonLink, EASE, MaskedLines } from "@/components/blocks";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-ink">
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.img
          src={IMG.hero}
          alt="Athlete training in a sophisticated performance environment"
          fetchPriority="high"
          className="h-[120%] w-full object-cover"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-ink" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />
      <div
        aria-hidden="true"
        className="absolute -left-32 top-1/4 h-[34rem] w-[34rem] rounded-full bg-limed opacity-[0.18] blur-[140px]"
      />

      <div className="relative z-10 flex min-h-[100svh] flex-col justify-end px-6 pb-10 pt-32 sm:px-10 lg:px-16">
        <div className="mx-auto w-full max-w-[1440px]">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-bone/80"
          >
            <span className="inline-block h-1.5 w-1.5 bg-lime" />
            StrengthLeap — Live 1:1 Human Performance
          </motion.p>

          <h1 className="mt-6 font-display text-[10.5vw] font-black uppercase leading-[0.92] tracking-tight text-white sm:text-[7.5vw] lg:text-[5.6vw]">
            <MaskedLines
              delay={0.25}
              lines={[
                "Private 1:1 performance",
                <>
                  coaching. In <span className="text-lime">real time.</span>
                </>,
                "Anywhere in the world.",
              ]}
            />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8, ease: EASE }}
            className="mt-10"
          >
            <p className="max-w-2xl text-base leading-relaxed text-bone/80 sm:text-lg">
              No generic app templates. No guessing your form. Work directly with an expert coach via
              live, private virtual sessions designed to eliminate pain, build strength, and prepare
              you for your biggest goals.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <ButtonLink to="/start" variant="lime" testid="hero-cta-assessment">
                Schedule Your Live Movement Assessment
              </ButtonLink>
              <ButtonLink to="/programs" variant="outlineLight" testid="hero-cta-programs">
                Explore the 4 Coaching Pathways
              </ButtonLink>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            data-testid="hero-credibility-ribbon"
            className="mt-12 flex flex-wrap items-center gap-3 border-t border-white/10 pt-6"
          >
            {BRAND.credibility.split("·").map((c) => (
              <span
                key={c}
                className="flex items-center gap-2.5 border border-lime/30 bg-lime/10 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-lime"
              >
                <span className="inline-block h-1 w-1 bg-lime" />
                {c.trim()}
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
